import {
  disclosure,
  disclosureCss,
  kitPartCss,
  panel,
  panelCss,
} from '../armKit';
import { deepWorkFacts } from '../pageData';
import { viewedUser } from '../profileFacts';
import { WARM_HAIRLINE } from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';

type Downloadable = {
  workId: number;
  title: string;
  url: string;
  downloadUrl: string;
};

const SHELF_SIZE = 6;
const ROWS_CLASS = 'profile-liautotest-shelf__rows';
const TITLE_CLASS = 'profile-liautotest-shelf__title';
const LINK_CLASS = 'profile-liautotest-shelf__link';
const READ_SOURCE = 'profile-liautotest-shelf-read';
const DOWNLOAD_SOURCE = 'profile-liautotest-shelf-download';
const SEE_ALL_SOURCE = 'profile-liautotest-shelf-see-all';

function armCss(scope: string): string {
  return (
    panelCss(scope, { frame: 'warm', marginBottom: '0' }) +
    disclosureCss(scope) +
    kitPartCss('panelTitle', scope, 'font-size: 16px;') +
    kitPartCss('summary', scope, 'margin-top: 8px;') +
    `
${scope} .${ROWS_CLASS} {
  list-style: none;
  padding: 0;
  margin: 0;
}
${scope} .${ROWS_CLASS} li {
  display: flex;
  align-items: baseline;
  gap: 14px;
  padding: 6px 0;
  border-top: 1px solid ${WARM_HAIRLINE};
}
${scope} .${ROWS_CLASS} li:first-child {
  border-top: 0;
}
${scope} .${TITLE_CLASS} {
  flex: 1 1 auto;
  font-size: 14px;
  line-height: 1.35;
}
${scope} .${LINK_CLASS} {
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}
`
  );
}

function link(
  label: string,
  href: string,
  onClick: () => void,
): HTMLAnchorElement {
  const anchor = document.createElement('a');
  anchor.className = LINK_CLASS;
  anchor.href = href;
  anchor.textContent = label;
  anchor.addEventListener('click', onClick);

  return anchor;
}

// The Download anchors here are the arm's own, over the works_json
// download_url; the served per-card Download anchors below stay untouched.
function rowsOf(entries: Downloadable[], ctx: ArmContext): HTMLElement {
  const list = document.createElement('ul');
  list.className = ROWS_CLASS;

  entries.forEach((entry) => {
    const item = document.createElement('li');

    const title = document.createElement('span');
    title.className = TITLE_CLASS;
    title.textContent = entry.title;
    item.appendChild(title);

    item.appendChild(
      link('Read', entry.url, () =>
        ctx.track(READ_SOURCE, { entityId: entry.workId }),
      ),
    );
    item.appendChild(
      link('Download', entry.downloadUrl, () =>
        ctx.track(DOWNLOAD_SOURCE, { entityId: entry.workId }),
      ),
    );

    list.appendChild(item);
  });

  return list;
}

// Newest is highest work id: works_json comes back in the profile's own
// section order, exposes no created_at, and work ids are creation-ordered.
function downloadablesOf(facts: Map<number, WorkFacts>): Downloadable[] {
  return [...facts]
    .flatMap(([workId, work]) =>
      work.downloadUrl != null && work.title != null && work.url != null
        ? [
            {
              workId,
              title: work.title,
              url: work.url,
              downloadUrl: work.downloadUrl,
            },
          ]
        : [],
    )
    .sort((a, b) => b.workId - a.workId);
}

// No figure anywhere on the shelf: the rows come off a capped fetch, so on
// exactly the biggest profiles a printed count would be wrong, and a wrong
// number is worse than none. "uploads", not "papers": the shelf lists every
// downloadable work, and the corpus includes book chapters a reader would not
// call a paper.
function headline(firstName: string): string {
  return `Downloads from ${firstName}'s uploads`;
}

export const downloadShelf: ArmSpec = {
  onReady: async (ctx) => {
    const firstName = viewedUser()?.first_name;
    if (firstName == null || firstName === '') return;

    const downloadables = downloadablesOf(
      await deepWorkFacts(ctx.viewedUserId),
    );
    if (downloadables.length === 0) return;

    const slot = ctx.mountSlot('columnTop');
    if (slot == null) return;

    ctx.injectStyles(armCss(ctx.scope));

    const { root, body } = panel({ title: headline(firstName) });
    body.appendChild(rowsOf(downloadables.slice(0, SHELF_SIZE), ctx));

    const rest = downloadables.slice(SHELF_SIZE);
    if (rest.length > 0) {
      body.appendChild(
        // 'See more', never 'See all N': past the fetch's cap the rest is not
        // all, and its length is not the corpus's N.
        disclosure('See more', rowsOf(rest, ctx), () =>
          ctx.track(SEE_ALL_SOURCE),
        ),
      );
    }

    slot.appendChild(root);
    ctx.markRendered();
  },
};
